import { useState } from 'react';
import { COMPONENT_ART } from './componentArt.jsx';
import { Board, SLOTS } from './PlacementExercise.jsx';

/**
 * Follow the data.
 *
 * The section that turns hardware knowledge into security reasoning.
 * Everything else in this lab teaches what the parts are; this asks
 * where a particular piece of data actually is at a given moment, and
 * lets four facts the lesson states be derived instead:
 *
 *   why investigators image memory before pulling the plug
 *   why an hour of unsaved work vanishes and saved work does not
 *   why a deleted file is so often recoverable
 *   why firmware malware survives wiping the disk
 *
 * Each scenario is a short chain of steps. At every step the learner
 * says which component holds the data now, and the board lights that
 * part up as the trail builds - so the answer is a place on a real
 * board rather than a word.
 */

const PARTS = [
  { id: 'io', name: 'Input/Output' },
  { id: 'cpu', name: 'CPU' },
  { id: 'ram', name: 'RAM' },
  { id: 'storage', name: 'Storage' },
  { id: 'network', name: 'Network Adapter' },
  { id: 'motherboard', name: 'Motherboard firmware' },
];

const SCENARIOS = [
  {
    id: 'password',
    icon: '🔑',
    title: 'You type your password',
    setup: 'You are signing in to a website. Follow the password from your fingers to the server.',
    steps: [
      {
        prompt: 'You press the keys. Where does the password enter the machine?',
        answer: 'io',
        why: 'The keyboard is an input device. Every character starts its journey here.',
      },
      {
        prompt: 'The browser is holding what you have typed so far. Where does it sit while you type?',
        answer: 'ram',
        why: 'A running program keeps its working data in memory. The password is sitting in RAM in plain text.',
      },
      {
        prompt: 'You press Enter. Which part sends it to the website?',
        answer: 'network',
        why: 'It leaves through the network adapter — encrypted in transit, but it existed unencrypted in memory first.',
      },
    ],
    insight:
      'Notice where it never went: storage. Your password is never written to the disk, which is exactly why it cannot be recovered from one — and exactly why anyone investigating a live machine captures its memory before touching the power.',
  },
  {
    id: 'essay',
    icon: '📝',
    title: 'You write an essay',
    setup: 'An hour of typing, then you finally press Save. Follow the work.',
    steps: [
      {
        prompt: 'You have typed for an hour and not saved once. Where does that work exist?',
        answer: 'ram',
        why: 'Only in memory. Nothing has been written anywhere permanent yet.',
      },
      {
        prompt: 'You press Save. Where does it go so it survives being switched off?',
        answer: 'storage',
        why: 'Saving writes it to the disk, which keeps its contents with no power.',
      },
    ],
    insight:
      'That gap is the whole reason unsaved work is lost in a power cut. For an hour it lived somewhere that forgets — and autosave exists precisely to close that gap.',
  },
  {
    id: 'plug',
    icon: '🔌',
    title: 'Someone pulls the plug',
    setup: 'The machine was running, unlocked, with an encrypted drive mounted. The power is cut without warning.',
    steps: [
      {
        prompt: 'Which part loses everything it was holding, instantly?',
        answer: 'ram',
        why: 'Memory is volatile. Without power it holds nothing at all.',
      },
      {
        prompt: 'Which part still has the saved files the next morning?',
        answer: 'storage',
        why: 'The disk keeps its contents indefinitely with no power at all.',
      },
      {
        prompt: 'The drive was encrypted, and the key was in use. Where was that key?',
        answer: 'ram',
        why: 'A mounted encrypted drive keeps its key in memory — so pulling the plug destroyed the one thing that could have opened it.',
      },
    ],
    insight:
      'This is the whole reason there is a right and a wrong way to seize a running computer. Pull the plug and you keep the files but lose the key, the passwords and every trace of what was running. Capture the memory first and you keep both.',
  },
  {
    id: 'wipe',
    icon: '🧹',
    title: 'The infection that came back',
    setup: 'A machine is infected. It is wiped completely and the operating system reinstalled — and within a day it is infected again.',
    steps: [
      {
        prompt: 'Wiping and reinstalling erases which part?',
        answer: 'storage',
        why: 'A wipe clears the disk. Everything the operating system and your files live in is gone.',
      },
      {
        prompt: 'The infection returned anyway. Which part was never touched by that wipe?',
        answer: 'motherboard',
        why: 'The board carries firmware that runs before the operating system exists. A disk wipe never goes near it.',
      },
    ],
    insight:
      'Firmware malware is rare and serious for exactly this reason: it sits below everything you would normally clean. The usual answer — reinstall and move on — simply does not reach it.',
  },
];

export default function FollowTheData({ onBack, onSolved }) {
  const [active, setActive] = useState(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);      // one picked component id per step
  const [done, setDone] = useState(new Set());

  const scenario = SCENARIOS.find((s) => s.id === active) ?? null;
  const current = scenario?.steps[step] ?? null;
  const finished = scenario && step >= scenario.steps.length;

  // What was picked on this step, and the parts got right on the way
  // here. Both read off `answers` rather than being kept alongside it,
  // so stepping back cannot leave the board lit for an answer that is
  // no longer on screen.
  const picked = answers[step] ?? null;
  const trail = scenario
    ? answers.slice(0, step + 1).filter((a, i) => a === scenario.steps[i]?.answer)
    : [];

  const start = (id) => {
    setActive(id);
    setStep(0);
    setAnswers([]);
  };

  const choose = (partId) => {
    if (picked !== null) return;
    setAnswers((prev) => {
      const next = prev.slice();
      next[step] = partId;
      return next;
    });
  };

  const next = () => {
    if (step + 1 >= scenario.steps.length) {
      const after = new Set(done).add(scenario.id);
      setDone(after);
      if (after.size === SCENARIOS.length) onSolved?.();
    }
    setStep((s) => s + 1);
  };

  /**
   * Back retraces the way in, one move at a time: a step of the trace,
   * then the scenario list, and only then out of the section. Dropping
   * someone out of the whole task because they wanted to re-read the
   * previous question is the quickest way to lose the thread.
   */
  const back = !scenario
    ? { label: '← Back to Placement', act: onBack }
    : step > 0
      ? { label: '← Back a step', act: () => setStep((s) => s - 1) }
      : { label: '← Back to the scenarios', act: () => setActive(null) };

  return (
    <section className="lab-section">
      <div className="place-head">
        <div>
          <h2>Follow the Data</h2>
          <p className="lab-lead">
            Knowing what the parts are is one thing. This asks where a particular piece of data
            actually is, moment by moment — which is where hardware turns into security.
          </p>
        </div>
        <div className="row gap-sm">
          <button type="button" className="btn btn-sm btn-ghost" onClick={back.act}>
            {back.label}
          </button>
          <span className="pill">{done.size} / {SCENARIOS.length} traced</span>
        </div>
      </div>

      {/* ---------- pick a scenario ---------- */}
      {!scenario && (
        <div className="trace-picker">
          {SCENARIOS.map((s) => (
            <button
              key={s.id}
              type="button"
              className={`trace-card ${done.has(s.id) ? 'is-done' : ''}`}
              onClick={() => start(s.id)}
            >
              <span className="trace-icon" aria-hidden="true">{s.icon}</span>
              <strong>{s.title}</strong>
              <p>{s.setup}</p>
              {done.has(s.id) && <span className="trace-tick">✓ traced</span>}
            </button>
          ))}
        </div>
      )}

      {/* ---------- trace one scenario ---------- */}
      {scenario && (
        <div className="trace-run">
          <div className="trace-scene">
            <span className="trace-icon" aria-hidden="true">{scenario.icon}</span>
            <div>
              <strong>{scenario.title}</strong>
              <p className="small muted">{scenario.setup}</p>
            </div>
          </div>

          <div className="place-layout">
            <aside className="place-tray">
              <h3>{finished ? 'The trail' : 'Where is the data now?'}</h3>

              {!finished && (
                <>
                  <p className="trace-prompt">{current.prompt}</p>
                  <div className="place-parts">
                    {PARTS.map((p) => {
                      const Art = COMPONENT_ART[p.id];
                      const isRight = p.id === current.answer;
                      const state =
                        picked === null ? '' : isRight ? 'is-right' : picked === p.id ? 'is-wrong' : '';
                      return (
                        <button
                          key={p.id}
                          type="button"
                          className={`place-part trace-part ${state}`}
                          onClick={() => choose(p.id)}
                          disabled={picked !== null}
                        >
                          <span className="place-part-art">{Art ? <Art /> : null}</span>
                          <strong>{p.name}</strong>
                        </button>
                      );
                    })}
                  </div>

                  {picked !== null && (
                    <>
                      <p className={`lab-feedback ${picked === current.answer ? 'is-right' : 'is-wrong'}`}>
                        {picked === current.answer
                          ? `✓ ${current.why}`
                          : `✗ Not quite. ${current.why}`}
                      </p>
                      <button type="button" className="btn btn-sm" onClick={next}>
                        {step + 1 >= scenario.steps.length ? 'See what this tells you →' : 'Next step →'}
                      </button>
                    </>
                  )}
                </>
              )}

              {finished && (
                <>
                  <ol className="trace-list">
                    {scenario.steps.map((s) => {
                      const part = PARTS.find((p) => p.id === s.answer);
                      return (
                        <li key={s.prompt}>
                          <strong>{part?.name}</strong>
                          <span className="small muted">{s.why}</span>
                        </li>
                      );
                    })}
                  </ol>
                  <button type="button" className="btn btn-sm" onClick={() => setActive(null)}>
                    ← Choose another
                  </button>
                </>
              )}
            </aside>

            {/* The board lights up as the trail builds. Once the run is
                over it shows the whole correct path, so the summary
                beside it and the board agree even if a step was missed. */}
            <div className="place-board">
              <Board />
              {SLOTS.map((s) => {
                const shown = finished ? scenario.steps.map((x) => x.answer) : trail;
                const order = shown.indexOf(s.accepts);
                const lit = order !== -1;
                const isNow = lit && !finished && order === trail.length - 1;
                return (
                  <div
                    key={s.id}
                    className={`place-slot trace-slot ${lit ? 'is-lit' : ''} ${isNow ? 'is-now' : ''}`}
                    style={{ left: `${s.x}%`, top: `${s.y}%`, width: `${s.w}%`, height: `${s.h}%` }}
                  >
                    {lit && (
                      <span className="trace-step-mark">{order + 1}</span>
                    )}
                  </div>
                );
              })}
              {/* the board itself has no slot, so firmware lights the whole thing */}
              {(finished ? scenario.steps.some((x) => x.answer === 'motherboard') : trail.includes('motherboard')) && (
                <div className="trace-board-lit" aria-hidden="true" />
              )}
            </div>
          </div>

          {finished && (
            <div className="lab-done">
              <div>
                <strong>What this tells you</strong>
                <p style={{ margin: '0.3rem 0 0' }}>{scenario.insight}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {done.size === SCENARIOS.length && !scenario && (
        <div className="lab-done">
          <strong>All four traced.</strong> You can now answer the question that matters on a real
          machine: if I do this, what survives and what is gone forever?
        </div>
      )}
    </section>
  );
}
